/**
 * Back-office view of a candidate's onboarding dossier.
 *
 * Shown on the candidate profile once status is `validated` or later. Displays:
 *  - The onboarding lifecycle (sent → opened → in_progress → submitted → approved)
 *  - The candidate's answers (compact grid)
 *  - The uploaded documents with a "Voir" button (auth'd stream via /api/onboarding/candidates/:id/doc/:kind)
 *  - Two admin actions: Approve / Ask for fix
 *  - A "Renvoyer le lien" button that resends the onboarding invite.
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    Check,
    CheckCircle2,
    Clock,
    Copy,
    Eye,
    FileText,
    History,
    Loader2,
    Mail,
    MoveHorizontal,
    RotateCcw,
    Send,
    ShieldCheck,
    Trash2,
    Upload,
    UserRound,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { hasRole } from "../../lib/roles";
import EditInfoButton from "./EditInfoButton";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const STATUS_META = {
    sent: { label: "Lien envoyé", tone: "bg-sky-50 border-sky-200 text-sky-700", Icon: Send },
    opened: { label: "Ouvert par le candidat", tone: "bg-indigo-50 border-indigo-200 text-indigo-700", Icon: Eye },
    in_progress: { label: "En cours de remplissage", tone: "bg-amber-50 border-amber-200 text-amber-800", Icon: Clock },
    submitted: { label: "Dossier reçu — à valider", tone: "bg-emerald-50 border-emerald-300 text-emerald-800", Icon: CheckCircle2 },
    approved: { label: "Dossier approuvé", tone: "bg-emerald-500 border-emerald-600 text-white", Icon: ShieldCheck },
    needs_fix: { label: "Demandes de correction envoyées", tone: "bg-red-50 border-red-200 text-red-700", Icon: AlertCircle },
};

const DOC_LABELS = {
    rib: "RIB",
    identity: "Pièce d'identité",
    vitale: "Carte Vitale",
    address_proof: "Justificatif domicile",
    badge_photo: "Photo badge",
    handicap: "RQTH",
    student_card: "Carte étudiante",
    hosting_attestation: "Attestation hébergement",
    hosting_host_id: "CNI hébergeur",
};

// Documents required for every candidate — displayed with an upload
// slot even when the candidate hasn't uploaded yet (so the admin can
// upload on behalf of a non-tech-savvy candidate — see Bogdan Edouard
// Penard scenario). Order matches the priority the CEO wants to see
// in the drop-zone.
const REQUIRED_DOCS = [
    "rib",
    "identity",
    "vitale",
    "address_proof",
    "badge_photo",
];

// Iter70 — audit-log date formatter. Uses fr-FR locale ("01/08 à 15:42")
// with a fallback to the raw ISO string if the browser can't parse. Kept
// outside the component so it isn't rebuilt on every render.
function formatAuditDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
        return iso;
    }
}


/**
 * Iter101 · CEO 2026-09-07 — Déplacer un document d'un slot à l'autre
 * (ex : le candidat a uploadé son titre de séjour dans "CNI hébergeur"
 * au lieu de "Pièce d'identité"). Rendu inline dans chaque slot occupé,
 * réservé aux admins.
 */
function MoveDocMenu({ currentKind, onMove }) {
    const targets = Object.entries(DOC_LABELS).filter(([k]) => k !== currentKind);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid={`move-doc-${currentKind}`}
                    className="h-8 w-8 rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft inline-flex items-center justify-center transition"
                    title="Déplacer vers un autre slot"
                >
                    <MoveHorizontal className="h-3.5 w-3.5" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Déplacer vers…
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {targets.map(([k, label]) => (
                    <DropdownMenuItem
                        key={k}
                        onClick={() => onMove(k)}
                        data-testid={`move-doc-target-${k}`}
                        className="text-[13px] font-semibold"
                    >
                        <MoveHorizontal className="h-3 w-3 mr-2 text-slate-400" />
                        {label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}


/**
 * Iter102d · CEO 2026-09-08 — Ajouter un document supplémentaire dans un
 * slot optionnel (RQTH, carte étudiante, attestation hébergement, CNI
 * hébergeur…). Utile quand le candidat envoie un doc complémentaire par
 * mail. Réservé aux admins.
 */
function AddOptionalDocMenu({ existingKinds, onPick }) {
    const fileInputRef = useRef(null);
    const [pendingKind, setPendingKind] = useState(null);
    const available = Object.entries(DOC_LABELS).filter(
        ([k]) => !existingKinds.includes(k),
    );
    if (!available.length) return null;
    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        data-testid="add-optional-doc-menu"
                        className="rounded-2xl border border-dashed border-slate-300 bg-white hover:border-shariti-orange hover:bg-shariti-orange-soft/20 p-3 flex items-center gap-2.5 transition text-left group cursor-pointer w-full"
                    >
                        <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0 group-hover:scale-110 transition">
                            <Upload className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-extrabold text-shariti-black">
                                + Ajouter un document
                            </div>
                            <div className="text-[10px] font-medium text-slate-500">
                                Choisir un slot optionnel (RQTH, hébergement…)
                            </div>
                        </div>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                    <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Type de document à ajouter
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {available.map(([k, label]) => (
                        <DropdownMenuItem
                            key={k}
                            data-testid={`add-optional-doc-${k}`}
                            onClick={() => {
                                setPendingKind(k);
                                // Laisser le state s'appliquer avant d'ouvrir le picker
                                setTimeout(() => fileInputRef.current?.click(), 50);
                            }}
                            className="text-[13px] font-semibold"
                        >
                            <Upload className="h-3 w-3 mr-2 text-slate-400" />
                            {label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && pendingKind) {
                        onPick(pendingKind, file);
                    }
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setPendingKind(null);
                }}
            />
        </>
    );
}


const FIELD_LABELS = {
    civilite: "Civilité", last_name: "Nom", first_name: "Prénom", maiden_name: "Nom de jeune fille",
    birth_date: "Né(e) le", nationality: "Nationalité", social_security_number: "N° Sécu",
    birth_city: "Ville de naissance", birth_dept: "Département", birth_country: "Pays",
    address_line1: "Adresse", address_city: "Ville", address_postal_code: "CP",
    email: "Email", phone_mobile: "Mobile", phone_landline: "Fixe",
    emergency_name: "Contact urgence", emergency_phone_mobile: "Urgence · mobile", emergency_phone_landline: "Urgence · fixe",
    marital_status: "Statut", spouse_name: "Conjoint", nb_adults_dependent: "Adultes à charge", nb_children_dependent: "Enfants à charge",
    foreign_status: "Statut étranger", permit_number: "N° titre séjour", permit_expiry: "Expire le",
};


export default function OnboardingCard({ candidate, user }) {
    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null);
    const [sending, setSending] = useState(false);
    const [acting, setActing] = useState(null); // "approve" | "needs_fix"
    const [askFix, setAskFix] = useState(false);
    const [fixNote, setFixNote] = useState("");
    // iter110n-4 fix3 · CEO 2026-09-29 · Le Coordinateur RH avec la
    // permission "Valider / rejeter / demander correction" doit pouvoir
    // approuver et demander une correction de dossier, exactement comme
    // un admin. Le backend re-guard toujours — c'est juste une UX
    // prophylactique pour ne pas cacher les boutons.
    const canReview = hasRole(user, "admin") || (
        hasRole(user, "coordinateur") && !!user?.can_review_onboarding
    );
    // Admin-upload state — track which doc_kind is currently being
    // uploaded so we show a spinner in that specific slot without
    // freezing the whole card.
    const [uploadingKind, setUploadingKind] = useState(null);
    const [deletingKind, setDeletingKind] = useState(null);
    const fileInputsRef = useRef({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/onboarding/candidates/${candidate.id}`);
            setRecord(data);
        } catch (e) {
            // No form yet — that's fine, we'll show a CTA to send the invite.
            setRecord(null);
        } finally {
            setLoading(false);
        }
    }, [candidate.id]);
    useEffect(() => { load(); }, [load]);

    async function resend() {
        setSending(true);
        try {
            const { data } = await api.post(`/onboarding/candidates/${candidate.id}/send`);
            if (data.provider === "bounced") {
                toast.error("Email rejeté par le serveur", {
                    description: data.email_error || "Vérifiez l'adresse email du candidat",
                });
            } else {
                toast.success("Email d'onboarding envoyé ✅", {
                    description: `Provider : ${data.provider}`,
                });
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    }

    async function approve() {
        setActing("approve");
        try {
            await api.post(`/onboarding/candidates/${candidate.id}/approve`);
            toast.success("Dossier approuvé 🎉");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setActing(null);
        }
    }

    async function requestFix() {
        setActing("needs_fix");
        try {
            const { data } = await api.post(
                `/onboarding/candidates/${candidate.id}/needs-fix`,
                { note: fixNote },
            );
            if (data?.ok) {
                toast.success("Email de correction envoyé au candidat", {
                    description: fixNote,
                });
            } else {
                toast.warning(
                    "Correction enregistrée mais l'email n'est pas parti",
                    { description: data?.email_error || "SMTP en échec" },
                );
            }
            setAskFix(false);
            setFixNote("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setActing(null);
        }
    }

    async function openDoc(kind) {
        try {
            const res = await api.get(`/onboarding/candidates/${candidate.id}/doc/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            // Free blob URL after a short delay (browsers keep the tab alive)
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error("Impossible d'ouvrir le document");
        }
    }

    async function openDocHistory(storagePath) {
        // Fetch a previous version from the archive. Used by the
        // "Versions précédentes" chips under each doc slot when the
        // candidate has replaced their identity/RIB/etc.
        try {
            const res = await api.get(
                `/onboarding/candidates/${candidate.id}/doc-history/${encodeURI(storagePath)}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error("Impossible d'ouvrir cette version");
        }
    }

    async function moveDoc(fromKind, toKind) {
        // CEO 2026-09-07 : « le candidat a uploadé son titre de séjour
        // dans le slot CNI hébergeur au lieu de Pièce d'identité — je
        // dois pouvoir déplacer le fichier sans lui redemander ».
        try {
            await api.post(`/onboarding/candidates/${candidate.id}/move-doc`, {
                from_kind: fromKind,
                to_kind: toKind,
            });
            toast.success(`Document déplacé vers ${DOC_LABELS[toKind] || toKind}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Déplacement impossible");
        }
    }

    async function adminUpload(kind, file) {
        if (!file) return;
        setUploadingKind(kind);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(
                `/onboarding/candidates/${candidate.id}/upload/${kind}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(`${DOC_LABELS[kind] || kind} · uploadé`);
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error(`Upload impossible : ${msg}`);
        } finally {
            setUploadingKind(null);
            // Reset the <input type="file"> so the same file can be re-selected
            const ref = fileInputsRef.current[kind];
            if (ref) ref.value = "";
        }
    }

    async function adminDelete(kind) {
        if (!window.confirm(`Supprimer ${DOC_LABELS[kind] || kind} du dossier ?`)) return;
        setDeletingKind(kind);
        try {
            await api.delete(`/onboarding/candidates/${candidate.id}/upload/${kind}`);
            toast.success(`${DOC_LABELS[kind] || kind} supprimé`);
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error(`Suppression impossible : ${msg}`);
        } finally {
            setDeletingKind(null);
        }
    }

    async function copyLink() {
        // We don't expose the token in the admin GET (privacy) — a resend
        // regenerates a fresh link server-side. So "copy link" fires a resend
        // and copies the freshly-returned URL. Idempotent.
        try {
            const { data } = await api.post(`/onboarding/candidates/${candidate.id}/send`);
            await navigator.clipboard.writeText(data.url);
            toast.success("Lien copié — nouveau lien généré et email renvoyé");
            await load();
        } catch (e) {
            toast.error("Erreur");
        }
    }

    if (loading) {
        return (
            <div className="card-soft p-6 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
            </div>
        );
    }

    // No dossier yet — show CTA
    if (!record) {
        return (
            <div className="card-soft p-6" data-testid="onboarding-card-empty">
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                        <Send className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-black text-shariti-black">Dossier d'embauche</h3>
                        <p className="text-sm font-medium text-slate-600 mt-1">
                            Aucun dossier n'a encore été envoyé à {candidate.first_name}. Deux options :
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button
                                onClick={resend}
                                disabled={sending}
                                data-testid="send-onboarding-invite"
                                className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white h-11 px-5 font-black text-xs uppercase tracking-widest hover:bg-shariti-black transition disabled:opacity-60"
                            >
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                Envoyer le dossier au candidat
                            </button>
                            {canReview && (
                                <label
                                    data-testid="admin-bootstrap-upload"
                                    className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-orange text-shariti-orange h-11 px-5 font-black text-xs uppercase tracking-widest hover:bg-shariti-orange-soft cursor-pointer transition"
                                >
                                    <Upload className="h-4 w-4" />
                                    Ouvrir & uploader (admin)
                                    <input
                                        type="file"
                                        accept="application/pdf,image/*"
                                        className="hidden"
                                        onChange={(e) => adminUpload("rib", e.target.files?.[0])}
                                    />
                                </label>
                            )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2 italic">
                            L&apos;option admin bootstrappe le dossier sans envoyer d&apos;email — utile pour les candidats non-tech (dépôt en main propre).
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const meta = STATUS_META[record.status] || STATUS_META.sent;
    const Icon = meta.Icon;
    const data = record.data || {};
    const docs = record.documents || {};
    // Regroupement des versions archivées par doc_kind pour que chaque
    // slot puisse afficher "Versions précédentes" (l'ancien passeport
    // reste consultable après upload du titre de séjour).
    const historyByKind = {};
    for (const entry of (record.documents_history || [])) {
        if (!entry?.doc_kind) continue;
        (historyByKind[entry.doc_kind] ||= []).push(entry);
    }
    for (const k of Object.keys(historyByKind)) {
        historyByKind[k].sort((a, b) => {
            const va = new Date(a.archived_at || a.uploaded_at || 0).getTime();
            const vb = new Date(b.archived_at || b.uploaded_at || 0).getTime();
            return vb - va;
        });
    }

    // Compact "key info" grid — only shows filled fields, in a stable order
    const filledFields = Object.keys(FIELD_LABELS).filter((k) => data[k] !== undefined && data[k] !== null && data[k] !== "");

    return (
        <div className="card-soft p-6 space-y-5" data-testid="onboarding-card">
            {/* Header */}
            <div className="flex items-start gap-4 flex-wrap">
                <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                    <UserRound className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-black text-shariti-black">Dossier d'embauche</h3>
                    <div className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 border text-[11px] font-bold ${meta.tone}`}>
                        <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </div>
                    <div className="text-[11px] font-medium text-slate-400 mt-1">
                        Envoyé le {new Date(record.sent_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}
                        {record.submitted_at && <> · Reçu le {new Date(record.submitted_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}</>}
                        {record.approved_at && <> · Approuvé le {new Date(record.approved_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })} par {record.approved_by_name}</>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={copyLink} data-testid="onboarding-copy-link" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-9 px-3 text-[11px] font-bold">
                        <Copy className="h-3 w-3" /> Copier le lien
                    </button>
                    {/* iter101 · CEO 2026-09-07 : « J'ai cliqué Renvoyer sur
                        un dossier soumis et ça a renvoyé Bienvenue complétez
                        votre dossier — c'est incohérent. » Le bouton est
                        maintenant status-aware :
                          - `submitted` / `approved` : désactivé (le RH doit
                            d'abord demander une correction pour rouvrir la
                            fiche — bouton "Demander correction" plus bas)
                          - `needs_fix` : libellé "Renvoyer la correction"
                          - autre : "Renvoyer" (invitation initiale) */}
                    {(() => {
                        const s = record.status;
                        const isLocked = s === "submitted" || s === "approved";
                        const isNeedsFix = s === "needs_fix";
                        const label = isNeedsFix ? "Renvoyer la correction" : "Renvoyer";
                        return (
                            <button
                                onClick={resend}
                                disabled={sending || isLocked}
                                data-testid="onboarding-resend"
                                title={isLocked
                                    ? "Dossier déjà soumis — utilisez « Demander correction » pour rouvrir la fiche"
                                    : (isNeedsFix
                                        ? "Renvoie l'email de correction (avec un nouveau lien)"
                                        : "Renvoie le lien d'invitation initial")}
                                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-orange text-shariti-orange hover:bg-shariti-orange-soft h-9 px-3 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                                {label}
                            </button>
                        );
                    })()}
                </div>
            </div>

            {/* Admin admin note (if needs_fix was requested) */}
            {record.admin_note && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-3">
                    <div className="text-[11px] font-black uppercase tracking-widest text-red-800 mb-1">Corrections demandées</div>
                    <div className="text-sm font-medium text-red-900 italic">« {record.admin_note} »</div>
                </div>
            )}

            {/* Filled data */}
            {filledFields.length > 0 && (
                <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Informations candidat</div>
                        {canReview && <EditInfoButton candidateId={candidate.id} testid="onboarding-edit-info" />}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 rounded-2xl bg-shariti-cream/50 border border-shariti-gray/40 p-4">
                        {filledFields.map((k) => (
                            <div key={k} className="text-[12px]">
                                <span className="font-semibold text-slate-500">{FIELD_LABELS[k]} : </span>
                                <span className="font-bold text-shariti-black break-words">{String(data[k])}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Documents — admin can upload/replace on behalf of candidates
                who aren't tech-savvy (Bogdan scenario). Slots are always
                shown for the 5 required docs; extra optional ones (RQTH,
                carte étudiante, hébergement…) appear only if uploaded. */}
            <div>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Documents ({Object.keys(docs).length}/{REQUIRED_DOCS.length} requis)
                    </div>
                    {canReview && (
                        <span className="text-[10px] font-bold text-shariti-orange" data-testid="onboarding-admin-upload-hint">
                            {record.status === "submitted" || record.status === "approved"
                                ? `Upload admin actif — override ${record.status === "submitted" ? "soumis" : "approuvé"} tracé dans l'audit`
                                : "Upload admin activé — utile pour candidats non-tech"}
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {/* Required slots — always visible, with upload/replace UI for admins */}
                    {REQUIRED_DOCS.map((kind) => {
                        const doc = docs[kind];
                        // iter102d (CEO 2026-09-08) : l'admin peut désormais uploader
                        // MÊME sur un form submitted/approved (workflow complément
                        // par mail). L'override est tracé dans documents_audit_log.
                        // iter110n-4 fix3 · Coordinateur RH avec can_review_onboarding
                        // a exactement les mêmes droits d'édition qu'un admin.
                        const canEdit = canReview;
                        const isUploading = uploadingKind === kind;
                        const isDeleting = deletingKind === kind;
                        return (
                            <div
                                key={kind}
                                data-testid={`doc-slot-${kind}`}
                                className={`rounded-2xl border p-3 flex items-start gap-2.5 transition ${
                                    doc
                                        ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                                        : "border-dashed border-slate-300 bg-slate-50"
                                }`}
                            >
                                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${
                                    doc ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-400"
                                }`}>
                                    {isUploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : doc ? (
                                        <Check className="h-4 w-4" strokeWidth={3} />
                                    ) : (
                                        <FileText className="h-4 w-4" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-extrabold text-shariti-black truncate">
                                        {DOC_LABELS[kind] || kind}
                                    </div>
                                    <div className="text-[10px] font-medium text-slate-500 truncate">
                                        {doc
                                            ? doc.original_filename
                                            : (canEdit ? "Uploader depuis mon poste" : "Non uploadé")}
                                    </div>
                                    {doc?.uploaded_by === "admin" && (
                                        <div className="text-[9px] font-black uppercase tracking-widest text-shariti-orange mt-0.5">
                                            Uploadé par admin
                                        </div>
                                    )}
                                    {(historyByKind[kind] || []).length > 0 && (
                                        <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`doc-history-${kind}`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                                {historyByKind[kind].length} version{historyByKind[kind].length > 1 ? "s" : ""} archivée{historyByKind[kind].length > 1 ? "s" : ""}
                                            </span>
                                            {historyByKind[kind].map((h) => (
                                                <button
                                                    key={h.storage_path}
                                                    type="button"
                                                    onClick={() => openDocHistory(h.storage_path)}
                                                    data-testid={`doc-history-item-${kind}`}
                                                    title={`Uploadé le ${formatAuditDate(h.uploaded_at)} · Remplacé le ${formatAuditDate(h.archived_at)}`}
                                                    className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-300 hover:border-shariti-orange hover:bg-shariti-orange-soft/40 text-slate-600 hover:text-shariti-orange px-1.5 py-0.5 text-[10px] font-bold transition"
                                                >
                                                    <History className="h-2.5 w-2.5" />
                                                    <span className="max-w-[110px] truncate">{h.original_filename || "version précédente"}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {doc && (
                                        <button
                                            type="button"
                                            onClick={() => openDoc(kind)}
                                            data-testid={`open-doc-${kind}`}
                                            className="h-8 w-8 rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft inline-flex items-center justify-center transition"
                                            title="Voir"
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                    {doc && canReview && (
                                        <MoveDocMenu currentKind={kind} onMove={(to) => moveDoc(kind, to)} />
                                    )}
                                    {canEdit && (
                                        <>
                                            <label
                                                data-testid={`admin-upload-${kind}`}
                                                className={`h-8 w-8 rounded-full inline-flex items-center justify-center cursor-pointer transition ${
                                                    isUploading
                                                        ? "text-shariti-orange bg-shariti-orange-soft"
                                                        : "text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft"
                                                }`}
                                                title={doc ? "Remplacer" : "Uploader (admin)"}
                                            >
                                                <Upload className="h-3.5 w-3.5" />
                                                <input
                                                    ref={(el) => { if (el) fileInputsRef.current[kind] = el; }}
                                                    type="file"
                                                    accept="application/pdf,image/*"
                                                    className="hidden"
                                                    disabled={isUploading || isDeleting}
                                                    onChange={(e) => adminUpload(kind, e.target.files?.[0])}
                                                />
                                            </label>
                                            {doc && (
                                                <button
                                                    type="button"
                                                    onClick={() => adminDelete(kind)}
                                                    disabled={isDeleting}
                                                    data-testid={`admin-delete-${kind}`}
                                                    className="h-8 w-8 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center transition disabled:opacity-40"
                                                    title="Supprimer"
                                                >
                                                    {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {/* Optional extras — only rendered if the candidate uploaded them */}
                    {Object.entries(docs)
                        .filter(([kind]) => !REQUIRED_DOCS.includes(kind))
                        .map(([kind, doc]) => (
                            <div
                                key={kind}
                                data-testid={`doc-slot-${kind}`}
                                className="rounded-2xl border border-slate-200 bg-white hover:border-shariti-orange transition p-3 flex items-start gap-2.5"
                            >
                                <button
                                    onClick={() => openDoc(kind)}
                                    data-testid={`open-doc-${kind}`}
                                    className="flex items-start gap-2.5 flex-1 min-w-0 text-left"
                                >
                                    <div className="h-9 w-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                        <Check className="h-4 w-4" strokeWidth={3} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-extrabold text-shariti-black truncate">{DOC_LABELS[kind] || kind}</div>
                                        <div className="text-[10px] font-medium text-slate-500 truncate">{doc.original_filename}</div>
                                        {(historyByKind[kind] || []).length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`doc-history-${kind}`}>
                                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                                    {historyByKind[kind].length} archivée{historyByKind[kind].length > 1 ? "s" : ""}
                                                </span>
                                                {historyByKind[kind].map((h) => (
                                                    <span
                                                        key={h.storage_path}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => { e.stopPropagation(); openDocHistory(h.storage_path); }}
                                                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openDocHistory(h.storage_path); } }}
                                                        data-testid={`doc-history-item-${kind}`}
                                                        title={`Uploadé le ${formatAuditDate(h.uploaded_at)} · Remplacé le ${formatAuditDate(h.archived_at)}`}
                                                        className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-300 hover:border-shariti-orange hover:bg-shariti-orange-soft/40 text-slate-600 hover:text-shariti-orange px-1.5 py-0.5 text-[10px] font-bold transition cursor-pointer"
                                                    >
                                                        <History className="h-2.5 w-2.5" />
                                                        <span className="max-w-[110px] truncate">{h.original_filename || "version précédente"}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <Eye className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                                </button>
                                {canReview && (
                                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 shrink-0">
                                        <label
                                            data-testid={`admin-replace-optional-${kind}`}
                                            className="h-8 w-8 rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft inline-flex items-center justify-center cursor-pointer transition"
                                            title="Remplacer"
                                        >
                                            <Upload className="h-3.5 w-3.5" />
                                            <input
                                                type="file"
                                                accept="application/pdf,image/*"
                                                className="hidden"
                                                onChange={(e) => adminUpload(kind, e.target.files?.[0])}
                                            />
                                        </label>
                                        <MoveDocMenu currentKind={kind} onMove={(to) => moveDoc(kind, to)} />
                                    </div>
                                )}
                            </div>
                        ))}
                    {/* iter102d · CEO 2026-09-08 : bouton "Ajouter un document"
                        pour uploader dans un slot optionnel (RQTH, carte étudiante,
                        attestation hébergement, CNI hébergeur…). Rendu admin only. */}
                    {canReview && (
                        <AddOptionalDocMenu
                            existingKinds={Object.keys(docs)}
                            onPick={(kind, file) => adminUpload(kind, file)}
                        />
                    )}
                </div>
            </div>

            {/* Iter70 — Journal d'audit lisible (URSSAF / DGCCRF).
                Ordre chronologique inversé (plus récent en haut). Rendu
                UNIQUEMENT si des mouvements ont eu lieu, pour ne pas
                alourdir la fiche des dossiers fraîchement envoyés. */}
            {Array.isArray(record.documents_audit_log) && record.documents_audit_log.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4" data-testid="onboarding-audit-log">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="h-8 w-8 rounded-xl bg-white text-slate-500 flex items-center justify-center border border-slate-200 shrink-0">
                            <History className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Journal d&apos;audit</div>
                            <div className="text-[11px] font-medium text-slate-500">Traçabilité URSSAF / DGCCRF — {record.documents_audit_log.length} mouvement{record.documents_audit_log.length > 1 ? "s" : ""}</div>
                        </div>
                    </div>
                    <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                        {[...record.documents_audit_log].reverse().map((entry, idx) => {
                            const verb = entry.action === "uploaded" ? "a uploadé"
                                : entry.action === "replaced" ? "a remplacé"
                                : "a supprimé";
                            const who = entry.actor === "admin"
                                ? (entry.actor_name || "un admin")
                                : `${candidate.first_name} ${candidate.last_name || ""}`.trim();
                            const dotClass = entry.action === "deleted"
                                ? "bg-red-400"
                                : entry.action === "replaced"
                                ? "bg-amber-400"
                                : "bg-emerald-400";
                            const badgeClass = entry.actor === "admin"
                                ? "bg-shariti-orange-soft text-shariti-orange"
                                : "bg-slate-200 text-slate-600";
                            return (
                                <li
                                    key={idx}
                                    data-testid="audit-log-entry"
                                    className="flex items-start gap-2.5 text-[12px] font-medium text-slate-700"
                                >
                                    <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${dotClass}`} />
                                    <div className="flex-1 min-w-0">
                                        <div>
                                            <span className={`inline-block ${badgeClass} rounded-full px-1.5 py-0 text-[9px] font-black uppercase tracking-widest mr-1.5 align-middle`}>
                                                {entry.actor === "admin" ? "admin" : "candidat"}
                                            </span>
                                            {entry.admin_override_locked_status && (
                                                <span
                                                    data-testid="audit-log-override-badge"
                                                    className="inline-block bg-red-100 text-red-700 rounded-full px-1.5 py-0 text-[9px] font-black uppercase tracking-widest mr-1.5 align-middle"
                                                    title={`Override sur dossier ${entry.admin_override_locked_status}`}
                                                >
                                                    override
                                                </span>
                                            )}
                                            <span className="font-bold text-shariti-black">{who}</span>
                                            <span> </span>
                                            <span>{verb}</span>
                                            <span> </span>
                                            <span className="font-black text-shariti-black">{DOC_LABELS[entry.doc_kind] || entry.doc_kind}</span>
                                            {entry.filename && (
                                                <span className="text-slate-400 italic truncate"> · {entry.filename}</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] font-medium text-slate-400 mt-0.5">
                                            {formatAuditDate(entry.at)}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Admin actions on the dossier — visibles dès qu'un dossier
                existe et n'est pas encore approuvé. Historiquement limité
                à `status === "submitted"` (attente d'action candidat),
                mais l'admin peut désormais uploader lui-même les docs
                (iter71) et doit pouvoir valider directement sans attendre
                que le candidat clique "Soumettre". Le backend accepte
                l'approbation quel que soit le statut d'entrée. */}
            {canReview && record.status !== "approved" && !askFix && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-shariti-gray/50">
                    <button
                        onClick={approve}
                        disabled={acting === "approve"}
                        data-testid="onboarding-approve"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white h-10 px-5 font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition disabled:opacity-60"
                    >
                        {acting === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        {record.status === "submitted" ? "Approuver le dossier" : "Valider ce dossier (admin)"}
                    </button>
                    {record.status === "submitted" && (
                        <button
                            onClick={() => setAskFix(true)}
                            data-testid="onboarding-request-fix"
                            className="inline-flex items-center gap-2 rounded-full bg-white border border-red-300 text-red-700 h-10 px-5 font-black text-xs uppercase tracking-widest hover:bg-red-50 transition"
                        >
                            <AlertCircle className="h-4 w-4" /> Demander une correction
                        </button>
                    )}
                </div>
            )}

            {canReview && askFix && (
                <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4">
                    <div className="text-[11px] font-black uppercase tracking-widest text-red-800 mb-2">Que doit corriger {candidate.first_name} ?</div>
                    <textarea
                        value={fixNote}
                        onChange={(e) => setFixNote(e.target.value)}
                        rows={3}
                        placeholder="Ex. Le RIB téléchargé est illisible — merci d'en fournir un plus net."
                        data-testid="onboarding-fix-note"
                        className="w-full rounded-xl border border-red-200 bg-white p-3 text-sm font-medium text-slate-800 focus:border-red-500 focus:outline-none"
                    />
                    <div className="mt-3 flex gap-2">
                        <button onClick={() => { setAskFix(false); setFixNote(""); }} className="rounded-full bg-white border border-slate-200 text-slate-600 h-9 px-4 text-[11px] font-bold">
                            Annuler
                        </button>
                        <button
                            onClick={requestFix}
                            disabled={!fixNote.trim() || acting === "needs_fix"}
                            data-testid="onboarding-fix-confirm"
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white h-9 px-4 text-[11px] font-black uppercase tracking-widest hover:bg-red-700 transition disabled:opacity-60"
                        >
                            {acting === "needs_fix" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Envoyer la demande
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
